import * as React from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@helpers/cn';
import { sidebarWidth, sidebarWidthMobile } from '@helpers/constants.js';
import { MenuIcon } from 'lucide-react';
import { Button, useTailwindBreakpoint } from '@eye-able/ui';
import {
  SidebarContext,
  type SidebarContextValue,
  useSidebar,
} from './SidebarContext';

type SlotProps = {
  children?: React.ReactNode;
  className?: string;
} & Record<string, unknown>;

function Slot({ children, className, ...props }: SlotProps) {
  if (!React.isValidElement(children)) {
    return null;
  }

  const childProps = children.props as Record<string, unknown> & {
    className?: string;
  };
  return React.cloneElement(children, {
    ...childProps,
    ...props,
    className: cn(childProps.className, className),
  });
}

type InputProps = React.ComponentProps<'input'>;
function Input({ className, ...props }: InputProps) {
  return (
    <input
      data-slot='input'
      className={cn(
        'eye:bg-background eye:placeholder:text-muted-foreground eye:flex eye:h-10 eye:w-full eye:rounded-md eye:border eye:border-black/10 eye:px-3 eye:py-2 eye:text-sm eye:outline-none eye:focus-visible:ring-2 eye:focus-visible:ring-offset-2 eye:disabled:cursor-not-allowed eye:disabled:opacity-50',
        className,
      )}
      {...props}
    />
  );
}

function Separator({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      role='separator'
      className={cn('eye:h-px eye:w-full eye:bg-black/10', className)}
      {...props}
    />
  );
}

function Skeleton({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      className={cn(
        'eye:animate-pulse eye:rounded-md eye:bg-black/10',
        className,
      )}
      {...props}
    />
  );
}

type SheetContextValue = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

const SheetContext = React.createContext<SheetContextValue | null>(null);

type SheetProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: React.ReactNode;
};

function Sheet({ open, onOpenChange, children }: SheetProps) {
  const contextValue = React.useMemo<SheetContextValue>(
    () => ({ open, onOpenChange }),
    [open, onOpenChange],
  );

  return (
    <SheetContext.Provider value={contextValue}>
      {children}
    </SheetContext.Provider>
  );
}

type SheetContentSide = 'left' | 'right' | 'top' | 'bottom';
type SheetContentProps = React.ComponentProps<'div'> & {
  side?: SheetContentSide;
};

function SheetContent({
  className,
  side = 'right',
  children,
  style,
  ...props
}: SheetContentProps) {
  const sheet = React.useContext(SheetContext);
  if (!sheet) {
    throw new Error('SheetContent must be used within a Sheet.');
  }

  const [shouldRender, setShouldRender] = React.useState(sheet.open);
  const [isVisible, setIsVisible] = React.useState(false);

  React.useEffect(() => {
    if (sheet.open) {
      setShouldRender(true);
      setIsVisible(false);
      requestAnimationFrame(() => setIsVisible(true));
      return;
    }

    setIsVisible(false);
    const timeout = window.setTimeout(() => setShouldRender(false), 200);
    return () => window.clearTimeout(timeout);
  }, [sheet.open]);

  React.useEffect(() => {
    if (!sheet.open) {
      return;
    }

    const scrollY = window.scrollY;
    const bodyStyle = document.body.style;
    const prevOverflow = bodyStyle.overflow;
    const prevPosition = bodyStyle.position;
    const prevTop = bodyStyle.top;
    const prevWidth = bodyStyle.width;

    // Prevent background scroll while the Sheet is open (including iOS).
    bodyStyle.overflow = 'hidden';
    bodyStyle.position = 'fixed';
    bodyStyle.top = `-${scrollY}px`;
    bodyStyle.width = '100%';

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        sheet.onOpenChange(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      bodyStyle.overflow = prevOverflow;
      bodyStyle.position = prevPosition;
      bodyStyle.top = prevTop;
      bodyStyle.width = prevWidth;
      window.scrollTo(0, scrollY);
    };
  }, [sheet]);

  const render = sheet.open || shouldRender;

  if (!render) {
    return null;
  }

  const sideClassName: Record<SheetContentSide, string> = {
    left: 'eye:left-0 eye:bottom-0',
    right: 'eye:right-0 eye:bottom-0',
    top: 'eye:top-0 eye:inset-x-0',
    bottom: 'eye:bottom-0 eye:inset-x-0',
  };

  const content = (
    <div
      className='eye:fixed eye:left-0 eye:right-0 eye:bottom-0 eye:z-[1300]'
      style={{
        top: 'var(--app-header-height, 75px)',
        height: 'calc(100svh - var(--app-header-height, 75px))',
      }}
    >
      <button
        type='button'
        aria-label='Close'
        className={cn(
          'eye:absolute eye:inset-0 eye:bg-black/40',
          isVisible ? 'eye:pointer-events-auto' : 'eye:pointer-events-none',
        )}
        onPointerDown={() => sheet.onOpenChange(false)}
        style={{
          opacity: isVisible ? 1 : 0,
          transition: 'opacity 200ms linear',
        }}
      />
      <div
        className={cn(
          'eye:absolute eye:flex eye:max-w-full eye:flex-col eye:outline-none',
          sideClassName[side],
          isVisible ? 'eye:pointer-events-auto' : 'eye:pointer-events-none',
          className,
        )}
        onClick={(event) => event.stopPropagation()}
        onPointerDown={(event) => event.stopPropagation()}
        {...props}
        style={{
          top: 0,
          height: '100%',
          width: 'var(--sidebar-width, 16rem)',
          maxWidth: '100vw',
          transform: isVisible
            ? 'translate3d(0, 0, 0)'
            : side === 'left'
              ? 'translate3d(-100%, 0, 0)'
              : side === 'right'
                ? 'translate3d(100%, 0, 0)'
                : side === 'top'
                  ? 'translate3d(0, -100%, 0)'
                  : 'translate3d(0, 100%, 0)',
          transition: 'transform 200ms ease',
          ...(style ?? {}),
        }}
      >
        {children}
      </div>
    </div>
  );

  if (typeof document === 'undefined') {
    return content;
  }

  return createPortal(content, document.body);
}

function SheetHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      className={cn('eye:flex eye:flex-col eye:gap-1.5 eye:p-4', className)}
      {...props}
    />
  );
}

function SheetTitle({ className, ...props }: React.ComponentProps<'h2'>) {
  return (
    <h2
      className={cn('eye:text-base eye:font-semibold', className)}
      {...props}
    />
  );
}

function SheetDescription({ className, ...props }: React.ComponentProps<'p'>) {
  return (
    <p className={cn('eye:text-sm eye:text-black/60', className)} {...props} />
  );
}

const SIDEBAR_COOKIE_NAME = 'sidebar_state';
const SIDEBAR_COOKIE_MAX_AGE = 60 * 60 * 24 * 7;
const SIDEBAR_WIDTH = `${sidebarWidth}px`;
const SIDEBAR_WIDTH_MOBILE = `${sidebarWidthMobile}px`;
const SIDEBAR_WIDTH_ICON = '3rem';
function SidebarProvider({
  defaultOpen = true,
  open: openProp,
  onOpenChange: setOpenProp,
  className,
  style,
  children,
  ...props
}: React.ComponentProps<'div'> & {
  defaultOpen?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}) {
  const isMobile = !useTailwindBreakpoint('xl');
  // This is the internal state of the sidebar.
  // We use openProp and setOpenProp for control from outside the component.
  const [_open, _setOpen] = React.useState(defaultOpen);
  const [_openMobile, _setOpenMobile] = React.useState(false);
  const open = openProp ?? _open;
  const setOpen = React.useCallback(
    (value: boolean | ((value: boolean) => boolean)) => {
      const openState = typeof value === 'function' ? value(open) : value;
      if (setOpenProp) {
        setOpenProp(openState);
      } else {
        _setOpen(openState);
      }
      // This sets the cookie to keep the sidebar state.
      if (typeof document !== 'undefined') {
        document.cookie = `${SIDEBAR_COOKIE_NAME}=${openState}; path=/; max-age=${SIDEBAR_COOKIE_MAX_AGE}`;
      }
    },
    [setOpenProp, open],
  );

  const setOpenMobile = React.useCallback(
    (value: boolean) => {
      _setOpenMobile(value);
    },
    [_setOpenMobile],
  );

  // Helper to toggle the sidebar.
  const toggleSidebar = React.useCallback(() => {
    if (isMobile) {
      _setOpenMobile((openValue) => !openValue);
      return;
    }
    setOpen((openValue) => !openValue);
  }, [_setOpenMobile, isMobile, setOpen]);

  React.useEffect(() => {
    if (!isMobile) {
      _setOpenMobile(false);
    }
  }, [isMobile]);

  const state = open ? 'expanded' : 'collapsed';
  const contextValue = React.useMemo<SidebarContextValue>(
    () => ({
      state,
      open,
      setOpen,
      isMobile,
      openMobile: _openMobile,
      setOpenMobile,
      toggleSidebar,
    }),
    [state, open, setOpen, isMobile, _openMobile, setOpenMobile, toggleSidebar],
  );
  return (
    <SidebarContext.Provider value={contextValue}>
      {/*<TooltipProvider delayDuration={0}>*/}
      <div
        data-slot='sidebar-wrapper'
        style={
          {
            '--sidebar-width': SIDEBAR_WIDTH,
            '--sidebar-width-icon': SIDEBAR_WIDTH_ICON,
            ...style,
          } as React.CSSProperties
        }
        className={cn(
          'eye:group/sidebar-wrapper eye:has-data-[variant=inset]:bg-sidebar eye:flex eye:min-h-svh eye:w-full',
          className,
        )}
        {...props}
      >
        {children}
      </div>
      {/*</TooltipProvider>*/}
    </SidebarContext.Provider>
  );
}
function Sidebar({
  side = 'left',
  variant = 'sidebar',
  collapsible = 'offcanvas',
  className,
  children,
  ...props
}: React.ComponentPropsWithoutRef<'div'> & {
  side?: 'left' | 'right';
  variant?: 'sidebar' | 'floating' | 'inset';
  collapsible?: 'offcanvas' | 'icon' | 'none';
}) {
  const { isMobile, state, openMobile, setOpenMobile } = useSidebar();

  if (collapsible === 'none') {
    return (
      <div
        data-slot='sidebar'
        className={cn(
          'eye:text-sidebar-foreground eye:flex eye:h-full eye:w-(--sidebar-width) eye:flex-col',
          className,
        )}
        {...props}
      >
        {children}
      </div>
    );
  }
  if (isMobile) {
    if (collapsible !== 'offcanvas') {
      const inlineSidebarHeight =
        'calc(100svh - var(--app-header-height, 75px))';

      return (
        <>
          {/* The drawer is dismissed by tapping the page behind it. That page
              is inert while the drawer is open, so it cannot receive the tap --
              this scrim catches it instead. Escape closes the drawer too. */}
          {openMobile ? (
            <div
              aria-hidden='true'
              onPointerDown={() => setOpenMobile(false)}
              style={{
                position: 'fixed',
                top: 'var(--app-header-height, 0px)',
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 39,
              }}
            />
          ) : null}
          <div
            data-sidebar='sidebar'
            data-slot='sidebar'
            data-mobile='true'
            className={cn(
              'eye:bg-sidebar eye:text-sidebar-foreground eye:flex eye:h-full eye:flex-col eye:overflow-hidden',
              side === 'left'
                ? 'eye:border-r eye:border-r-grey-20'
                : 'eye:border-l',
              className,
            )}
            {...props}
            style={{
              position: 'fixed',
              top: 'var(--app-header-height, 75px)',
              height: inlineSidebarHeight,
              maxHeight: inlineSidebarHeight,
              width: SIDEBAR_WIDTH_MOBILE,
              zIndex: 40,
              transition: 'transform 200ms ease',
              transform: openMobile
                ? 'translateX(0)'
                : side === 'left'
                  ? 'translateX(-100%)'
                  : 'translateX(100%)',
              left: side === 'left' ? 0 : undefined,
              right: side === 'right' ? 0 : undefined,
              pointerEvents: openMobile ? 'auto' : 'none',
              ...(props.style ?? {}),
            }}
          >
            <div
              className='eye:flex eye:h-full eye:w-full eye:min-h-0 eye:flex-col eye:overflow-hidden'
              style={{
                display: 'grid',
                gridTemplateRows: '1fr auto',
                minHeight: 0,
                overflow: 'hidden',
              }}
              {...(!openMobile && { inert: '' })}
            >
              {children}
            </div>
          </div>
        </>
      );
    }

    return (
      <Sheet open={openMobile} onOpenChange={setOpenMobile}>
        <SheetContent
          data-sidebar='sidebar'
          data-slot='sidebar'
          data-mobile='true'
          className='eye:bg-sidebar eye:text-sidebar-foreground eye:w-(--sidebar-width) eye:p-0 eye:[&>button]:hidden'
          style={
            {
              '--sidebar-width': SIDEBAR_WIDTH_MOBILE,
            } as React.CSSProperties
          }
          side={side}
          {...props}
        >
          <SheetHeader className='eye:sr-only'>
            <SheetTitle>Sidebar</SheetTitle>
            <SheetDescription>Displays the mobile sidebar.</SheetDescription>
          </SheetHeader>
          <div
            className='eye:flex eye:h-full eye:w-full eye:min-h-0 eye:flex-col eye:overflow-hidden'
            style={{
              display: 'grid',
              gridTemplateRows: '1fr auto',
              minHeight: 0,
              overflow: 'hidden',
            }}
          >
            {children}
          </div>
        </SheetContent>
      </Sheet>
    );
  }
  return (
    <div
      className='eye:text-sidebar-foreground eye:hidden eye:md:block eye:group eye:peer'
      data-state={state}
      data-collapsible={state === 'collapsed' ? collapsible : ''}
      data-variant={variant}
      data-side={side}
      data-slot='sidebar'
    >
      {/* This is what handles the sidebar gap on desktop */}
      <div
        data-slot='sidebar-gap'
        className={cn(
          'eye:relative eye:bg-transparent eye:transition-[width] eye:duration-200 eye:ease-linear',
          collapsible === 'offcanvas' ? 'eye:w-0' : 'eye:w-(--sidebar-width)',
          'eye:group-data-[side=right]:rotate-180',
          variant === 'floating' || variant === 'inset'
            ? 'eye:group-data-[collapsible=icon]:w-[calc(var(--sidebar-width-icon)+(--spacing(4)))]'
            : 'eye:group-data-[collapsible=icon]:w-(--sidebar-width-icon)',
        )}
      />
      <div
        data-slot='sidebar-container'
        className={cn(
          'eye:fixed eye:bottom-0 eye:z-10 eye:hidden eye:w-(--sidebar-width) eye:transition-[left,right,width] eye:duration-200 eye:ease-linear eye:md:flex',
          side === 'left'
            ? 'eye:left-0 eye:group-data-[collapsible=offcanvas]:left-[calc(var(--sidebar-width)*-1)]'
            : 'eye:right-0 eye:group-data-[collapsible=offcanvas]:right-[calc(var(--sidebar-width)*-1)]',
          // Adjust the padding for floating and inset variants.
          variant === 'floating' || variant === 'inset'
            ? cn(
                'eye:p-2 eye:group-data-[collapsible=icon]:w-[calc(var(--sidebar-width-icon)+(--spacing(4))+2px)]',
                variant === 'inset'
                  ? 'eye:group-data-[side=left]:border-r eye:group-data-[side=left]:border-r-grey-20 eye:group-data-[side=right]:border-l'
                  : null,
              )
            : 'eye:group-data-[collapsible=icon]:w-(--sidebar-width-icon) eye:group-data-[side=left]:border-r eye:group-data-[side=left]:border-r-grey-20 eye:group-data-[side=right]:border-l',
          className,
        )}
        {...props}
        style={{
          top: 'var(--app-header-height, 0px)',
          height: 'calc(100svh - var(--app-header-height, 0px))',
          ...(props.style ?? {}),
        }}
      >
        <div
          data-sidebar='sidebar'
          data-slot='sidebar-inner'
          className='eye:bg-sidebar eye:group-data-[variant=floating]:border-sidebar-border eye:flex eye:h-full eye:min-h-0 eye:w-full eye:flex-col eye:overflow-hidden eye:group-data-[variant=floating]:rounded-lg eye:group-data-[variant=floating]:border eye:group-data-[variant=floating]:shadow-sm'
          style={{
            display: 'grid',
            gridTemplateRows: '1fr auto',
            height: '100%',
            minHeight: 0,
            overflow: 'hidden',
          }}
        >
          {children}
        </div>
      </div>
    </div>
  );
}
function SidebarTrigger({
  className,
  onClick,
  ...props
}: React.ComponentProps<typeof Button>) {
  const { toggleSidebar } = useSidebar();
  return (
    <Button
      type='button'
      data-sidebar='trigger'
      data-slot='sidebar-trigger'
      variant='ghost'
      size='icon'
      className={cn('eye:size-7', className)}
      onClick={(event) => {
        onClick?.(event);
        toggleSidebar();
      }}
      {...props}
    >
      <MenuIcon />
      <span className='eye:sr-only'>Toggle Sidebar</span>
    </Button>
  );
}
function SidebarRail({ className, ...props }: React.ComponentProps<'button'>) {
  const { toggleSidebar } = useSidebar();
  return (
    <button
      data-sidebar='rail'
      data-slot='sidebar-rail'
      aria-label='Toggle Sidebar'
      tabIndex={-1}
      onClick={toggleSidebar}
      title='Toggle Sidebar'
      className={cn(
        'eye:hover:after:bg-sidebar-border eye:absolute eye:inset-y-0 eye:z-20 eye:hidden eye:w-4 eye:-translate-x-1/2 eye:transition-all eye:ease-linear eye:group-data-[side=left]:-right-4 eye:group-data-[side=right]:left-0 eye:after:absolute eye:after:inset-y-0 eye:after:left-1/2 eye:after:w-[2px] eye:sm:flex',
        'eye:in-data-[side=left]:cursor-w-resize eye:in-data-[side=right]:cursor-e-resize',
        'eye:[[data-side=left][data-state=collapsed]_&]:cursor-e-resize eye:[[data-side=right][data-state=collapsed]_&]:cursor-w-resize',
        'eye:hover:group-data-[collapsible=offcanvas]:bg-sidebar eye:group-data-[collapsible=offcanvas]:translate-x-0 eye:group-data-[collapsible=offcanvas]:after:left-full',
        'eye:[[data-side=left][data-collapsible=offcanvas]_&]:-right-2',
        'eye:[[data-side=right][data-collapsible=offcanvas]_&]:-left-2',
        className,
      )}
      {...props}
    />
  );
}
/**
 * The content area beside the sidebar: a container, not the page's <main>. The
 * footer is composed next to <main> inside it, because a contentinfo landmark
 * nested in main is not exposed as the page's footer.
 */
const SidebarInset = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<'div'>
>(function SidebarInset({ className, style, ...props }, ref) {
  const { isMobile, openMobile } = useSidebar();
  const isBehindDrawer = isMobile && openMobile;

  // `inert`, not aria-hidden: an aria-hidden subtree whose links and buttons
  // stay focusable is a WCAG failure -- keyboard focus lands in content the
  // screen reader cannot see. inert removes it from the accessibility tree and
  // the tab order both. It swallows pointer events as well, which is why the
  // tap-to-dismiss gesture lives on the sidebar's scrim.
  return (
    <div
      ref={ref}
      data-slot='sidebar-inset'
      {...(isBehindDrawer && { inert: '' })}
      className={cn(
        'eye:bg-background eye:relative eye:flex eye:w-full eye:flex-1 eye:flex-col',
        'eye:md:peer-data-[variant=inset]:m-2 eye:md:peer-data-[variant=inset]:ml-0 eye:md:peer-data-[variant=inset]:rounded-xl eye:md:peer-data-[variant=inset]:shadow-sm eye:md:peer-data-[variant=inset]:peer-data-[state=collapsed]:ml-2',
        className,
      )}
      style={{
        transform: isBehindDrawer
          ? 'translateX(var(--sidebar-width, 16rem))'
          : 'translateX(0)',
        transition: isMobile ? 'transform 200ms ease' : undefined,
        ...style,
      }}
      {...props}
    />
  );
});
function SidebarInput({
  className,
  ...props
}: React.ComponentProps<typeof Input>) {
  return (
    <Input
      data-slot='sidebar-input'
      data-sidebar='input'
      className={cn(
        'eye:bg-background eye:h-8 eye:w-full eye:shadow-none',
        className,
      )}
      {...props}
    />
  );
}
function SidebarHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot='sidebar-header'
      data-sidebar='header'
      className={cn('eye:flex eye:flex-col eye:gap-2 eye:p-2', className)}
      {...props}
    />
  );
}
// A <footer> would map to contentinfo here: on desktop the sidebar root chain
// is all divs, so nothing scopes it, and the page footer already owns that
// landmark. This is a container, not a landmark.
function SidebarFooter({
  className,
  style,
  ...props
}: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot='sidebar-footer'
      data-sidebar='footer'
      className={cn(
        'eye:bg-grey-20 eye:py-xs eye:px-s eye:flex eye:flex-col eye:min-w-0',
        className,
      )}
      style={{ flex: '0 0 auto', ...style }}
      {...props}
    />
  );
}
function SidebarSeparator({
  className,
  ...props
}: React.ComponentProps<typeof Separator>) {
  return (
    <Separator
      data-slot='sidebar-separator'
      data-sidebar='separator'
      className={cn('eye:bg-sidebar-border eye:mx-2 eye:w-auto', className)}
      {...props}
    />
  );
}
function SidebarContent({
  className,
  style,
  ...props
}: React.ComponentProps<'nav'>) {
  return (
    <nav
      data-slot='sidebar-content'
      data-sidebar='content'
      className={cn(
        'eye:flex eye:min-h-0 eye:flex-1 eye:flex-col eye:gap-2 eye:overflow-x-hidden eye:overflow-y-auto eye:group-data-[collapsible=icon]:overflow-hidden',
        className,
      )}
      style={{
        flex: '1 1 0',
        minHeight: 0,
        overflowX: 'hidden',
        overflowY: 'auto',
        ...style,
      }}
      {...props}
    />
  );
}
function SidebarGroup({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot='sidebar-group'
      data-sidebar='group'
      className={cn(
        'eye:relative eye:flex eye:w-full eye:min-w-0 eye:flex-col eye:p-2',
        className,
      )}
      {...props}
    />
  );
}
function SidebarGroupLabel({
  className,
  asChild = false,
  ...props
}: React.ComponentPropsWithoutRef<'div'> & { asChild?: boolean }) {
  const Comp = asChild ? Slot : 'div';
  return (
    <Comp
      data-slot='sidebar-group-label'
      data-sidebar='group-label'
      className={cn(
        'eye:text-sidebar-foreground/70 eye:ring-sidebar-ring eye:flex eye:h-8 eye:shrink-0 eye:items-center eye:rounded-md eye:px-2 eye:text-xs eye:font-medium eye:outline-hidden eye:transition-[margin,opacity] eye:duration-200 eye:ease-linear eye:focus-visible:ring-2 eye:[&>svg]:size-4 eye:[&>svg]:shrink-0',
        'eye:group-data-[collapsible=icon]:-mt-8 eye:group-data-[collapsible=icon]:opacity-0',
        className,
      )}
      {...props}
    />
  );
}
function SidebarGroupAction({
  className,
  asChild = false,
  ...props
}: React.ComponentPropsWithoutRef<'button'> & { asChild?: boolean }) {
  const Comp = asChild ? Slot : 'button';
  return (
    <Comp
      data-slot='sidebar-group-action'
      data-sidebar='group-action'
      className={cn(
        'eye:text-sidebar-foreground eye:ring-sidebar-ring eye:hover:bg-sidebar-accent eye:hover:text-sidebar-accent-foreground eye:absolute eye:top-3.5 eye:right-3 eye:flex eye:aspect-square eye:w-5 eye:items-center eye:justify-center eye:rounded-md eye:p-0 eye:outline-hidden eye:transition-transform eye:focus-visible:ring-2 eye:[&>svg]:size-4 eye:[&>svg]:shrink-0',
        // Increases the hit area of the button on mobile.
        'eye:after:absolute eye:after:-inset-2 eye:md:after:hidden',
        'eye:group-data-[collapsible=icon]:hidden',
        className,
      )}
      {...props}
    />
  );
}
function SidebarGroupContent({
  className,
  ...props
}: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot='sidebar-group-content'
      data-sidebar='group-content'
      className={cn('eye:w-full eye:text-sm', className)}
      {...props}
    />
  );
}
function SidebarMenu({ className, ...props }: React.ComponentProps<'ul'>) {
  return (
    <ul
      data-slot='sidebar-menu'
      data-sidebar='menu'
      className={cn(
        'eye:flex eye:w-full eye:min-w-0 eye:flex-col eye:gap-1',
        className,
      )}
      {...props}
    />
  );
}
function SidebarMenuItem({ className, ...props }: React.ComponentProps<'li'>) {
  return (
    <li
      data-slot='sidebar-menu-item'
      data-sidebar='menu-item'
      className={cn('eye:group/menu-item eye:relative', className)}
      {...props}
    />
  );
}
type SidebarMenuButtonVariant = 'default' | 'outline';
type SidebarMenuButtonSize = 'default' | 'sm' | 'lg';

const sidebarMenuButtonBaseClassName =
  'eye:peer/menu-button eye:flex eye:w-full eye:items-center eye:gap-2 eye:overflow-hidden eye:rounded-md eye:p-2 eye:text-left eye:text-sm eye:outline-hidden eye:ring-sidebar-ring eye:transition-[width,height,padding] eye:hover:bg-sidebar-accent eye:hover:text-sidebar-accent-foreground eye:focus-visible:ring-2 eye:active:bg-sidebar-accent eye:active:text-sidebar-accent-foreground eye:disabled:pointer-events-none eye:disabled:opacity-50 eye:group-has-data-[sidebar=menu-action]/menu-item:pr-8 eye:aria-disabled:pointer-events-none eye:aria-disabled:opacity-50 eye:data-[active=true]:bg-sidebar-accent eye:data-[active=true]:font-medium eye:data-[active=true]:text-sidebar-accent-foreground eye:data-[state=open]:hover:bg-sidebar-accent eye:data-[state=open]:hover:text-sidebar-accent-foreground eye:group-data-[collapsible=icon]:size-8! eye:group-data-[collapsible=icon]:p-2! eye:[&>span:last-child]:truncate eye:[&>svg]:size-4 eye:[&>svg]:shrink-0';

const sidebarMenuButtonVariantClassName: Record<
  SidebarMenuButtonVariant,
  string
> = {
  default:
    'eye:hover:bg-sidebar-accent eye:hover:text-sidebar-accent-foreground',
  outline:
    'eye:bg-background eye:shadow-[0_0_0_1px_hsl(var(--sidebar-border))] eye:hover:bg-sidebar-accent eye:hover:text-sidebar-accent-foreground eye:hover:shadow-[0_0_0_1px_hsl(var(--sidebar-accent))]',
};

const sidebarMenuButtonSizeClassName: Record<SidebarMenuButtonSize, string> = {
  default: 'eye:h-8 eye:text-sm',
  sm: 'eye:h-7 eye:text-xs',
  lg: 'eye:h-12 eye:text-sm eye:group-data-[collapsible=icon]:p-0!',
};

function sidebarMenuButtonVariants(options: {
  variant?: SidebarMenuButtonVariant;
  size?: SidebarMenuButtonSize;
}) {
  const variant = options.variant ?? 'default';
  const size = options.size ?? 'default';

  return cn(
    sidebarMenuButtonBaseClassName,
    sidebarMenuButtonVariantClassName[variant],
    sidebarMenuButtonSizeClassName[size],
  );
}
function SidebarMenuButton({
  asChild = false,
  isActive = false,
  variant = 'default',
  size = 'default',
  tooltip,
  className,
  ...props
}: React.ComponentPropsWithoutRef<'button'> & {
  asChild?: boolean;
  isActive?: boolean;
  tooltip?: string;
  variant?: SidebarMenuButtonVariant;
  size?: SidebarMenuButtonSize;
}) {
  const Comp = asChild ? Slot : 'button';
  const { isMobile, state } = useSidebar();
  const title =
    props.title ??
    (tooltip && state === 'collapsed' && !isMobile ? tooltip : undefined);
  return (
    <Comp
      data-slot='sidebar-menu-button'
      data-sidebar='menu-button'
      data-size={size}
      data-active={isActive}
      title={title}
      className={cn(sidebarMenuButtonVariants({ variant, size }), className)}
      {...props}
    />
  );
}
function SidebarMenuAction({
  className,
  asChild = false,
  showOnHover = false,
  ...props
}: React.ComponentPropsWithoutRef<'button'> & {
  asChild?: boolean;
  showOnHover?: boolean;
}) {
  const Comp = asChild ? Slot : 'button';
  return (
    <Comp
      data-slot='sidebar-menu-action'
      data-sidebar='menu-action'
      className={cn(
        'eye:text-sidebar-foreground eye:ring-sidebar-ring eye:hover:bg-sidebar-accent eye:hover:text-sidebar-accent-foreground eye:peer-hover/menu-button:text-sidebar-accent-foreground eye:absolute eye:top-1.5 eye:right-1 eye:flex eye:aspect-square eye:w-5 eye:items-center eye:justify-center eye:rounded-md eye:p-0 eye:outline-hidden eye:transition-transform eye:focus-visible:ring-2 eye:[&>svg]:size-4 eye:[&>svg]:shrink-0',
        // Increases the hit area of the button on mobile.
        'eye:after:absolute eye:after:-inset-2 eye:md:after:hidden',
        'eye:peer-data-[size=sm]/menu-button:top-1',
        'eye:peer-data-[size=default]/menu-button:top-1.5',
        'eye:peer-data-[size=lg]/menu-button:top-2.5',
        'eye:group-data-[collapsible=icon]:hidden',
        showOnHover &&
          'eye:peer-data-[active=true]/menu-button:text-sidebar-accent-foreground eye:group-focus-within/menu-item:opacity-100 eye:group-hover/menu-item:opacity-100 eye:data-[state=open]:opacity-100 eye:md:opacity-0',
        className,
      )}
      {...props}
    />
  );
}
function SidebarMenuBadge({
  className,
  ...props
}: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot='sidebar-menu-badge'
      data-sidebar='menu-badge'
      className={cn(
        'eye:text-sidebar-foreground eye:pointer-events-none eye:absolute eye:right-1 eye:flex eye:h-5 eye:min-w-5 eye:items-center eye:justify-center eye:rounded-md eye:px-1 eye:text-xs eye:font-medium eye:tabular-nums eye:select-none',
        'eye:peer-hover/menu-button:text-sidebar-accent-foreground eye:peer-data-[active=true]/menu-button:text-sidebar-accent-foreground',
        'eye:peer-data-[size=sm]/menu-button:top-1',
        'eye:peer-data-[size=default]/menu-button:top-1.5',
        'eye:peer-data-[size=lg]/menu-button:top-2.5',
        'eye:group-data-[collapsible=icon]:hidden',
        className,
      )}
      {...props}
    />
  );
}
function SidebarMenuSkeleton({
  className,
  showIcon = false,
  ...props
}: React.ComponentProps<'div'> & {
  showIcon?: boolean;
}) {
  // Random width between 50 to 90%.
  const width = React.useMemo(() => {
    return `${Math.floor(Math.random() * 40) + 50}%`;
  }, []);
  return (
    <div
      data-slot='sidebar-menu-skeleton'
      data-sidebar='menu-skeleton'
      className={cn(
        'eye:flex eye:h-8 eye:items-center eye:gap-2 eye:rounded-md eye:px-2',
        className,
      )}
      {...props}
    >
      {showIcon && (
        <Skeleton
          className='eye:size-4 eye:rounded-md'
          data-sidebar='menu-skeleton-icon'
        />
      )}
      <Skeleton
        className='eye:h-4 eye:max-w-(--skeleton-width) eye:flex-1'
        data-sidebar='menu-skeleton-text'
        style={
          {
            '--skeleton-width': width,
          } as React.CSSProperties
        }
      />
    </div>
  );
}
function SidebarMenuSub({ className, ...props }: React.ComponentProps<'ul'>) {
  return (
    <ul
      data-slot='sidebar-menu-sub'
      data-sidebar='menu-sub'
      className={cn(
        'eye:border-sidebar-border eye:mx-3.5 eye:flex eye:min-w-0 eye:translate-x-px eye:flex-col eye:gap-1 eye:border-l eye:px-2.5 eye:py-0.5',
        'eye:group-data-[collapsible=icon]:hidden',
        className,
      )}
      {...props}
    />
  );
}
function SidebarMenuSubItem({
  className,
  ...props
}: React.ComponentProps<'li'>) {
  return (
    <li
      data-slot='sidebar-menu-sub-item'
      data-sidebar='menu-sub-item'
      className={cn('eye:group/menu-sub-item eye:relative', className)}
      {...props}
    />
  );
}
function SidebarMenuSubButton({
  asChild = false,
  size = 'md',
  isActive = false,
  className,
  ...props
}: React.ComponentPropsWithoutRef<'a'> & {
  asChild?: boolean;
  size?: 'sm' | 'md';
  isActive?: boolean;
}) {
  const Comp = asChild ? Slot : 'a';
  return (
    <Comp
      data-slot='sidebar-menu-sub-button'
      data-sidebar='menu-sub-button'
      data-size={size}
      data-active={isActive}
      className={cn(
        'eye:text-sidebar-foreground eye:ring-sidebar-ring eye:hover:bg-sidebar-accent eye:hover:text-sidebar-accent-foreground eye:active:bg-sidebar-accent eye:active:text-sidebar-accent-foreground eye:[&>svg]:text-sidebar-accent-foreground eye:flex eye:h-7 eye:min-w-0 eye:-translate-x-px eye:items-center eye:gap-2 eye:overflow-hidden eye:rounded-md eye:px-2 eye:outline-hidden eye:focus-visible:ring-2 eye:disabled:pointer-events-none eye:disabled:opacity-50 eye:aria-disabled:pointer-events-none eye:aria-disabled:opacity-50 eye:[&>span:last-child]:truncate eye:[&>svg]:size-4 eye:[&>svg]:shrink-0',
        'eye:data-[active=true]:bg-sidebar-accent eye:data-[active=true]:text-sidebar-accent-foreground',
        size === 'sm' && 'eye:text-xs',
        size === 'md' && 'eye:text-sm',
        'eye:group-data-[collapsible=icon]:hidden',
        className,
      )}
      {...props}
    />
  );
}
export {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupAction,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInput,
  SidebarInset,
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSkeleton,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarProvider,
  SidebarRail,
  SidebarSeparator,
  SidebarTrigger,
};
